/**
 * PhonePrefixPicker — country dial-code picker for E.164 phone entry.
 *
 * Behavior:
 *   - Closed state: shows the selected country's flag emoji + "+{code}".
 *     Matches the compact look from the Henaket reference image.
 *   - Open state: dropdown listing every country that has a dial code
 *     (243 of the 249 ISO entries; uninhabited territories skipped).
 *     Each row: flag emoji, "+{dial}", country name in current UI language.
 *   - Search: substring match against translated name, English name,
 *     ISO code, and dial code itself. Prefix matches sort above
 *     substring matches.
 *   - Sort: alphabetical by translated country name. Matches the main
 *     Country picker convention for consistency.
 *
 * Why one row per ISO country (and not one per dial code):
 *   - Users think by country, not by dial code. "+1" alone is ambiguous
 *     (USA? Canada? Jamaica?) — the country name disambiguates.
 *   - Storage: we save the user's phone-country alpha-2 separately from
 *     the local digits. On submit we compose "+{dial_code}{local}" for
 *     the backend E.164 column.
 *
 * Props:
 *   - countries: list from /api/countries (0.36.381) (CountryOption[]). Each
 *     entry must have at minimum {code, name, dial_code}. Entries
 *     with no dial_code are filtered out.
 *   - value: the currently-selected ISO alpha-2 code, e.g. 'am'.
 *   - onChange(code): fires when the user picks a different country.
 *   - countryLabel(c): hook-provided function returning the translated
 *     name for a country row (we re-use the same fn the main Country
 *     picker uses, so dropdowns stay consistent).
 *   - hasError: optional boolean; when true, the trigger button picks up
 *     the standard field-error-bar styling on its parent wrapper. Set by
 *     the parent (CafRegister), not the picker itself.
 *
 * Accessibility:
 *   - Trigger is a <button> with aria-haspopup, aria-expanded.
 *   - Search input gets focus when the dropdown opens.
 *   - Arrow keys + Enter to navigate and pick (added in a future polish
 *     pass; for now mouse / touch + typing search works).
 *   - Escape closes the dropdown.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from './PhonePrefixPicker.module.css';
// 0.36.275 — Open-state panel now uses the canonical HenaketDropdown
// styling and focus model. The only structural difference from
// HenaketDropdown is that each option renders flag + dial code +
// country name combined into one row, since that's what the user is
// picking. The trigger (closed state) is unchanged — it stays the
// compact flag + dial-code chip that fits the phone-input prefix
// slot. Sharing classes guarantees that any future Henaket spec
// change reaches the phone picker automatically.
import henaket from './HenaketDropdown.module.css';
// 0.36.269 — Replaces the legacy regional-indicator-emoji rendering
// with SVG flags from the iso_country_flags library. FlagIcon
// handles the fetch + cache + fallback chain itself.
import FlagIcon from './FlagIcon';


// 0.36.275 — Canonical SearchIcon, copied from HenaketDropdown so the
// search input has the same magnifying-glass affordance.
const SearchIcon = () => (
  <svg
    /* 0.36.277 — className dropped; centered via flex parent. */
    viewBox="0 0 16 16"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="7" cy="7" r="5" />
    <path d="M 11 11 L 15 15" />
  </svg>
);


export default function PhonePrefixPicker({
  countries,
  value,
  onChange,
  countryLabel,
  hasError = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  // 0.36.275 — Canonical highlight focus model: the magenta border
  // appears on the highlighted row only when optionHighlightActive
  // is true. As soon as the search input gains focus (click or Tab),
  // it flips to false so the search input's own focus ring shows
  // alone — exactly one focus indicator visible at any time.
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [optionHighlightActive, setOptionHighlightActive] = useState(true);
  // 0.36.275 — Panel direction + max height, measured at open and on
  // resize. Same logic as HenaketDropdown's _measurePanel: if there's
  // room below, open down at MAX height; otherwise flip up if there's
  // more space above.
  const [panelDirection, setPanelDirection] = useState('down');
  const [panelMaxHeight, setPanelMaxHeight] = useState(320);
  // 0.36.277 — Panel width = field width. Measure the picker's parent
  // (HenaketPhoneInput's .row) on open and apply inline. Falls back
  // to the CSS default (`.panelOverride` width) when no parent is
  // available, so the picker still works in any future host that
  // doesn't wrap it in a field row.
  const [panelWidth, setPanelWidth] = useState(null);
  /* 0.49.72 - The panel is drawn on the page itself (a portal, fixed in
     the window), as HenaketDropdown's is, so a Modal or any box that
     scrolls no longer cuts it. It sits under (or over) the whole field:
     left and width from the field's row. */
  const [panelPos, setPanelPos] = useState({ left: 0, top: null, bottom: null });
  const panelRef = useRef(null);

  // Filter to countries that have a dial code. Uninhabited Antarctic
  // territories etc. drop out here — they're useless for phone entry.
  const dialCountries = useMemo(
    () => countries.filter(c => c.dial_code),
    [countries]
  );

  // Find the currently-selected country row. Falls back to first entry
  // if value doesn't match anything (defensive: should not happen, but a
  // bad initial value shouldn't crash the render).
  const selected = useMemo(
    () => dialCountries.find(c => c.code === value) || dialCountries[0] || null,
    [dialCountries, value]
  );

  // Sorted + filtered list shown in the open dropdown.
  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase().replace(/^\+/, '');
    let rows = dialCountries;
    if (term) {
      const scored = [];
      for (const c of dialCountries) {
        const label = countryLabel(c).toLowerCase();
        const english = (c.name || '').toLowerCase();
        const code = c.code.toLowerCase();
        const dial = c.dial_code.toLowerCase();
        let score = -1;
        if (label.startsWith(term) || english.startsWith(term)) score = 0;
        else if (code.startsWith(term) || dial.startsWith(term)) score = 1;
        else if (
          label.includes(term) || english.includes(term) ||
          code.includes(term) || dial.includes(term)
        ) score = 2;
        if (score >= 0) scored.push({ c, score, label });
      }
      scored.sort((a, b) => a.score - b.score || a.label.localeCompare(b.label));
      rows = scored.map(s => s.c);
    } else {
      rows = [...rows].sort((a, b) =>
        countryLabel(a).localeCompare(countryLabel(b))
      );
    }
    return rows;
  }, [dialCountries, search, countryLabel]);

  // 0.36.275 — When the visible rows change (open, search), re-aim the
  // highlight at the currently selected row if it's visible, or the
  // first row otherwise. Mirrors HenaketDropdown's openPanel: open
  // with the selected row pre-highlighted so the scroll-into-view
  // effect lands on it.
  useEffect(() => {
    if (!open) return;
    const idx = visibleRows.findIndex(c => c.code === value);
    setHighlightIndex(idx >= 0 ? idx : 0);
  }, [open, visibleRows, value]);

  // 0.36.275 — Measure available space and decide direction + height.
  // 0.36.277 — Same measurement also captures the parent's offsetWidth
  // so the panel can match the field width.
  const measurePanel = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const BUFFER = 8;
    const MAX = 320;
    const MIN = 120;
    const spaceBelow = viewportH - rect.bottom - BUFFER;
    const spaceAbove = rect.top - BUFFER;
    if (spaceBelow >= MAX) {
      setPanelDirection('down');
      setPanelMaxHeight(MAX);
    } else if (spaceAbove > spaceBelow) {
      setPanelDirection('up');
      setPanelMaxHeight(Math.min(MAX, Math.max(MIN, spaceAbove)));
    } else {
      setPanelDirection('down');
      setPanelMaxHeight(Math.min(MAX, Math.max(MIN, spaceBelow)));
    }
    // 0.36.277 — Parent width measurement. HenaketPhoneInput wraps
    // both the picker and the local-digits input in a single
    // `.row` flex container; measuring that gives us the full
    // field width (capped at 610 px per HenaketPhoneInput's own
    // rule). If the picker is consumed outside HenaketPhoneInput
    // and has no useful parent, we keep the previous CSS default
    // by leaving panelWidth null.
    const parent = containerRef.current.parentElement;
    const box = parent && parent.offsetWidth > 0 ? parent.getBoundingClientRect() : rect;
    const viewportW = window.innerWidth || document.documentElement.clientWidth;
    const width = Math.min(parent && parent.offsetWidth > 0 ? parent.offsetWidth : 320, viewportW - 2 * BUFFER);
    setPanelWidth(width);
    const GAP = 5;   // past the trigger's 3px focus ring, as HenaketDropdown
    const down = spaceBelow >= MAX || !(spaceAbove > spaceBelow);
    setPanelPos({
      left: Math.max(BUFFER, Math.min(box.left, viewportW - width - BUFFER)),
      top: down ? rect.bottom + GAP : null,
      bottom: down ? null : viewportH - rect.top + GAP,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) measurePanel();
  }, [open, measurePanel]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => measurePanel();
    window.addEventListener('resize', onResize);
    // 0.49.72 - And when the page or the Modal scrolls, the panel follows.
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, measurePanel]);

  // 0.36.275 — Scroll the highlighted row into view. Triggered on open
  // (lands on selected) and when keyboard nav moves the highlight
  // off-screen.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const items = listRef.current.querySelectorAll('[role="option"]');
    const node = items[highlightIndex];
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [open, highlightIndex]);

  // Click-outside-to-close.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      // 0.49.72 - The panel is outside the container now (portal).
      if (containerRef.current && !containerRef.current.contains(e.target)
          && !(panelRef.current && panelRef.current.contains(e.target))) {
        setOpen(false);
        setSearch('');
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // 0.36.275 — Match canonical: panel opens with the search NOT
  // auto-focused, so the magenta highlight sits on the selected row
  // (one focus indicator visible). Clicking the search field flips to
  // search-focus mode and the row highlight disappears. Previously we
  // autofocused on open — that put the search ring up immediately,
  // hiding which row was selected.

  const handlePick = (code) => {
    onChange(code);
    setOpen(false);
    setSearch('');
  };

  // 0.36.275 — Canonical keyboard model. ↓/↑ move the highlight (and
  // re-activate the row indicator if search had focus), Enter selects,
  // Esc handled by the document listener above.
  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOptionHighlightActive(true);
      setHighlightIndex(i => Math.min(visibleRows.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOptionHighlightActive(true);
      setHighlightIndex(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = visibleRows[highlightIndex];
      if (row) handlePick(row.code);
    }
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={`${styles.trigger} ${hasError ? styles.triggerError : ''}`}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={selected
          ? `Phone country: ${countryLabel(selected)} +${selected.dial_code}`
          : 'Phone country'}
      >
        {selected ? (
          <>
            <span className={styles.flag} aria-hidden="true">
              <FlagIcon code={selected.code} size={20} />
            </span>
            <span className={styles.code}>+{selected.dial_code}</span>
          </>
        ) : (
          <span className={styles.code}>—</span>
        )}
        <span className={styles.caret} aria-hidden="true">▾</span>
      </button>

      {open && createPortal(
        /* 0.36.275 — Canonical HenaketDropdown panel structure + classes.
           Container's positioning (left/right edges, width) comes from
           PhonePrefixPicker's own .panelOverride; everything inside
           (search input, divider, list, options, selected + highlighted
           states) inherits canonical visuals. */
        <div
          ref={panelRef}
          className={`${henaket.panel} ${panelDirection === 'up' ? henaket.panelUp : ''} ${styles.panelOverride} ${panelDirection === 'up' ? styles.panelOverrideUp : ''}`}
          style={{
            maxHeight: panelMaxHeight,
            position: 'fixed',
            left: `${panelPos.left}px`,
            right: 'auto',
            top: panelPos.top !== null ? `${panelPos.top}px` : 'auto',
            bottom: panelPos.bottom !== null ? `${panelPos.bottom}px` : 'auto',
            // 0.36.277 — Panel width = field width when we could
            // measure the parent. Falls back to the .panelOverride
            // CSS default (320 px) when not.
            ...(panelWidth ? { width: panelWidth } : {}),
          }}
        >
          {/* 0.36.277 — Canonical search box: self-contained
              bordered field, magnifying-glass affix on the right
              with a 1 px vertical separator. Same structure as
              HenaketDropdown — picker inherits the visual
              automatically since it imports the shared module. */}
          <div className={henaket.searchBox}>
            <input
              ref={searchRef}
              type="text"
              className={henaket.searchInput}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onFocus={() => setOptionHighlightActive(false)}
              onKeyDown={handleKeyDown}
              placeholder="Search country or code"
              aria-label="Search country or code"
              autoComplete="off"
            />
            <div className={henaket.searchSeparator} />
            <div className={henaket.searchIconWrap}>
              <SearchIcon />
            </div>
          </div>

          <div className={henaket.listBox}>
            {visibleRows.length === 0 ? (
              <div className={henaket.empty}>No matches</div>
            ) : (
              <ul ref={listRef} className={henaket.list} role="listbox">
                {visibleRows.map((c, idx) => {
                  const isSelected = c.code === value;
                  const isHighlighted = idx === highlightIndex;
                  const showBorder = optionHighlightActive && isHighlighted;
                  const optionClass = [
                    henaket.option,
                    isSelected && henaket.optionSelected,
                    showBorder && henaket.optionHighlighted,
                  ].filter(Boolean).join(' ');
                  return (
                    <li
                      key={c.code}
                      className={optionClass}
                      role="option"
                      aria-selected={isSelected}
                      onMouseEnter={() => {
                        setOptionHighlightActive(true);
                        setHighlightIndex(idx);
                        if (searchRef.current) searchRef.current.blur();
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handlePick(c.code)}
                    >
                      {/* 0.36.275 — Row content: flag + dial code +
                          country name. The ONLY structural difference
                          from a vanilla HenaketDropdown row (single
                          text label). Reuses the existing per-cell
                          spacing helpers from PhonePrefixPicker.module.css.
                          0.36.277 — Code + name spans flip to white text
                          when the row is selected (dark blue background),
                          since their per-span color: text-800 would
                          otherwise override the canonical .optionSelected
                          { color: white }. */}
                      <span className={styles.flag} aria-hidden="true">
                        <FlagIcon code={c.code} size={20} />
                      </span>
                      <span className={`${styles.rowCode} ${isSelected ? styles.rowCodeSelected : ''}`}>+{c.dial_code}</span>
                      <span className={`${styles.rowName} ${isSelected ? styles.rowNameSelected : ''}`}>{countryLabel(c)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
